import type { NoteFigure } from '../types';
import {
  applyRhythmFigureToBeat,
  createEmptyRhythmPattern,
  type RhythmMeasure,
  type RhythmPattern,
} from '../utils/rhythmPattern';

export interface RhythmChallenge {
  id: string;
  title: string;
  description: string;
  bpm: number;
  pattern: RhythmPattern;
}

type BeatDef = {
  figure: NoteFigure;
  hits: boolean[];
};

function buildMeasure(beatsPerMeasure: number, beats: BeatDef[]): RhythmMeasure {
  let measure: RhythmMeasure = {
    slots: createEmptyRhythmPattern(1, beatsPerMeasure).measures[0].slots,
  };

  for (let bi = 0; bi < beats.length; bi++) {
    const def = beats[bi];
    if (!def) continue;
    measure = applyRhythmFigureToBeat(
      measure,
      bi,
      def.figure,
      beatsPerMeasure,
    );
    const slot = measure.slots[bi];
    if (!slot?.active) continue;
    measure.slots[bi] = {
      ...slot,
      cells: slot.cells.map((c, ci) => ({
        ...c,
        hit: def.hits[ci] ?? false,
      })),
    };
  }

  return measure;
}

function buildPattern(
  beatsPerMeasure: number,
  measures: BeatDef[][],
): RhythmPattern {
  return {
    beatsPerMeasure,
    measures: measures.map((beats) => buildMeasure(beatsPerMeasure, beats)),
  };
}

export const RHYTHM_CHALLENGES: RhythmChallenge[] = [
  {
    id: 'taqui-ta',
    title: 'Taqui-Ta',
    description: 'Dos corcheas y una negra. ¡Escucha con atención!',
    bpm: 72,
    pattern: buildPattern(4, [
      [
        { figure: 'corchea', hits: [true, true] },
        { figure: 'negra', hits: [true] },
        { figure: 'corchea', hits: [true, true] },
        { figure: 'negra', hits: [true] },
      ],
    ]),
  },
  {
    id: 'silencio-sorpresa',
    title: 'Silencio sorpresa',
    description: 'Hay un tiempo que no suena. ¿Cuál es?',
    bpm: 70,
    pattern: buildPattern(4, [
      [
        { figure: 'negra', hits: [true] },
        { figure: 'negra', hits: [false] },
        { figure: 'negra', hits: [true] },
        { figure: 'negra', hits: [true] },
      ],
    ]),
  },
  {
    id: 'cuatro-corcheas',
    title: 'Semicorcheas veloz',
    description: 'Un tiempo lleno de sonidos rapiditos.',
    bpm: 68,
    pattern: buildPattern(4, [
      [
        { figure: 'negra', hits: [true] },
        { figure: 'semicorchea', hits: [true, true, true, true] },
        { figure: 'negra', hits: [true] },
        { figure: 'negra', hits: [true] },
      ],
    ]),
  },
  {
    id: 'dos-compases',
    title: 'Dos compases',
    description: 'El reto largo: escucha dos compases completos.',
    bpm: 76,
    pattern: buildPattern(4, [
      [
        { figure: 'corchea', hits: [true, true] },
        { figure: 'negra', hits: [true] },
        { figure: 'corchea', hits: [true, false] },
        { figure: 'negra', hits: [true] },
      ],
      [
        { figure: 'negra', hits: [true] },
        { figure: 'negra', hits: [true] },
        { figure: 'corchea', hits: [true, true] },
        { figure: 'corchea', hits: [true, true] },
      ],
    ]),
  },
];

export function getChallengeById(id: string): RhythmChallenge | undefined {
  return RHYTHM_CHALLENGES.find((c) => c.id === id);
}
